import React, { useEffect, useState } from 'react';
import {
  Alert, App, Button, DatePicker, Form, Input, InputNumber, Modal, Radio, Space, Tag, Typography, Upload, theme,
} from 'antd';
import Icon, {
  CameraOutlined, CarOutlined, ClockCircleOutlined, DeleteOutlined, EditOutlined, LoadingOutlined, PlusOutlined,
  ScanOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { inputNumberMoneyProps } from '../../utils/currency';
import {
  FisDugmesi, FisKucuk, ISARET_TANIM, IsaretEtiketleri, ciddiMi, fisiKucult, hata, para, tarih, useHarcirahApi,
} from './ortak';

/**
 * Seyahat giderleri (24.10.2026) — ulaşım (yakıt / uçak bileti / araç kiralama) ve konaklama. Giderleri ŞİRKET öder:
 * çalışana geri ödenmez, yemek harcırahına (toplam) eklenmez; maliyet takibi için belgesiyle girilir.
 * Ulaşım: şirket aracı → yalnız yakıt; uçak + kiralık araç → bilet, kiralama, yakıt. Konaklama isteğe bağlı, fatura
 * başına (giriş–çıkış, gece sayısı otomatik; belgede dönem varsa okunur).
 * 24.10.2026 ekran ayrımı: bilet, kiralama ve oteli REZERVASYON PERSONELİ girer (belge sonradan eklenebilir; çalışan
 * belgeler tamamlanınca gönderir), yakıtı ÇALIŞAN girer (belge zorunlu). GIDER_GIREN backend'deki eşiyle aynı.
 * routes/harcirah.py: /harcirah-benim|/harcirah-rezervasyon {id}/ulasim, /gider-oku, /gider; /harcirah-benim/gider-fis.
 * 30.10.2026: satırın başında belirgin tarih (konaklama giriş → çıkış, uçak uçuş tarihleri, diğerleri belge tarihi);
 * uçak bileti gidiş / dönüş / gidiş-dönüş, uçuş tarihleriyle (belge tarihi = biletin alındığı gün).
 */
const { Text } = Typography;
const { RangePicker } = DatePicker;
const MAKS_FIS = 10 * 1024 * 1024;

// antd'de uçak / akaryakıt / yatak simgesi yok — Material Icons yolları (Apache 2.0)
const svg = (d) => function Simge() {
  return <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d={d} /></svg>;
};
const UcakSvg = svg('M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z');
const YakitSvg = svg('M19.77 7.23l.01-.01-3.72-3.72L15 4.56l2.11 2.11c-.94.36-1.61 1.26-1.61 2.33 0 1.38 1.12 2.5 2.5 2.5.36 0 .69-.08 1-.21v7.21c0 .55-.45 1-1 1s-1-.45-1-1V14c0-1.1-.9-2-2-2h-1V5c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v16h10v-7.5h1.5v5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V9c0-.69-.28-1.32-.73-1.77zM12 10H6V5h6v5zm6 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z');
const OtelSvg = svg('M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z');
export const UcakIkon = (p) => <Icon component={UcakSvg} {...p} />;
export const YakitIkon = (p) => <Icon component={YakitSvg} {...p} />;
export const OtelIkon = (p) => <Icon component={OtelSvg} {...p} />;

export const GIDER_TANIM = {
  yakit: { ad: 'Yakıt', ekle: 'Yakıt Fişi', ikon: <YakitIkon />, ipucu: 'Güzergâh Ya Da Plaka (İsteğe Bağlı)' },
  ucak: { ad: 'Uçak Bileti', ekle: 'Uçak Bileti', ikon: <UcakIkon />, ipucu: 'Güzergâh / Uçuş No, ör. İST–ESB TK2120' },
  arac_kiralama: { ad: 'Araç Kiralama', ekle: 'Araç Kiralama', ikon: <CarOutlined />, ipucu: 'Kiralama Firması, Gün Sayısı' },
  konaklama: { ad: 'Konaklama', ekle: 'Konaklama', ikon: <OtelIkon />, ipucu: 'Otel Adı' },
};
export const ULASIM_TANIM = {
  sirket_araci: { ad: 'Şirket Aracı', ikon: <CarOutlined />, giderler: ['yakit'] },
  ucak: { ad: 'Uçak + Kiralık Araç', ikon: <UcakIkon />, giderler: ['ucak', 'arac_kiralama', 'yakit'] },
};
export const GIDER_GIREN = { yakit: 'calisan', ucak: 'rezervasyon', arac_kiralama: 'rezervasyon', konaklama: 'rezervasyon' };
// Backend UCAK_YONLERI / UCAK_YON_TARIHLERI ile aynı
export const UCAK_YON = {
  gidis: { ad: 'Gidiş', renk: 'blue', tarihler: ['gidis'] },
  donus: { ad: 'Dönüş', renk: 'purple', tarihler: ['donus'] },
  gidis_donus: { ad: 'Gidiş-Dönüş', renk: 'geekblue', tarihler: ['gidis', 'donus'] },
};
const SIRA = ['ucak', 'arac_kiralama', 'yakit', 'konaklama'];
const GIDER_NEDENLERI = {
  tutar: ['Belge yanlış okundu, doğru tutarı girdim', 'Belgede bu seyahatle ilgisiz kalem var',
    'Tutarın yalnız bu seyahate düşen payını girdim'],
  tarih: ['Belgedeki tarih yanlış okundu; doğru tarihleri girdim', 'Erken giriş / geç çıkış ya da plan değişti',
    'Fatura konaklamadan önce ya da sonra kesildi'],
};
const gun = (d) => d?.format('YYYY-MM-DD');
const kisaGun = (t) => `${t.slice(8, 10)}.${t.slice(5, 7)}`;
const GUN_KISA = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const gunAdiKisa = (t) => GUN_KISA[(dayjs(t).day() + 6) % 7];
// Giderin asıl tarihi (sıralama ve satır başı): konaklamada giriş, uçakta ilk uçuş, diğerlerinde belge tarihi
const anaTarih = (gd) => gd.giris || gd.gidis_tarihi || gd.donus_tarihi || gd.tarih || '';
// Konaklama gecesi listesi: giriş (dahil) – çıkış (hariç); mevcut fiyat korunur, yeni geceye `fiyat`
const geceKur = (donem, mevcut, fiyat) => {
  if (!donem?.[0] || !donem?.[1]) return [];
  const eski = Object.fromEntries((mevcut || []).map((x) => [x.tarih, x.tutar]));
  const n = donem[1].diff(donem[0], 'day');
  return Array.from({ length: Math.max(0, Math.min(n, 92)) }, (_, i) => {
    const t = donem[0].add(i, 'day').format('YYYY-MM-DD');
    return { tarih: t, tutar: eski[t] ?? fiyat ?? null };
  });
};

/** Gece şeridi (26.10.2026 "konaklama gün gün"): seyahatin her gecesi; dolu gece otel + fiyat, boş gece eklenebilir. */
function KonaklamaSeridi({ s, onGece, onAc }) {
  const geceler = {};
  (s.giderler || []).filter((gd) => gd.tur === 'konaklama').forEach((gd) => (gd.geceler || []).forEach((x) => {
    geceler[x.tarih] = { gd, tutar: x.tutar };
  }));
  const bas = dayjs(s.baslangic);
  const gunler = Array.from({ length: dayjs(s.bitis).diff(bas, 'day') + 1 }, (_, i) => bas.add(i, 'day').format('YYYY-MM-DD'));
  return (
    <div className="gd-serit" role="list" aria-label="Konaklama geceleri">
      {gunler.map((t) => {
        const g = geceler[t];
        const tikla = g ? (onAc && (() => onAc(g.gd))) : (onGece && (() => onGece(t)));
        return (
          <div key={t} role="listitem" className={`gd-gece${g ? ' gd-gece-dolu' : ''}${tikla ? ' gd-tikla' : ''}`}
            tabIndex={tikla ? 0 : undefined} onClick={tikla}
            onKeyDown={tikla ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tikla(); } } : undefined}>
            <div className="gd-gece-gun">{gunAdiKisa(t)} {kisaGun(t)}</div>
            {g ? (
              <>
                <div className="gd-gece-otel" title={g.gd.aciklama || ''}>{g.gd.aciklama || 'Otel'}</div>
                <div className="gd-gece-tutar">{para(g.tutar)}</div>
              </>
            ) : <div className="gd-gece-bos">{onGece ? <><PlusOutlined /> Gece Ekle</> : 'Otel Yok'}</div>}
          </div>
        );
      })}
    </div>
  );
}

export const giderDonemi = (gd) => {
  if (gd.tur === 'konaklama') return `${tarih(gd.giris)} – ${tarih(gd.cikis)} · ${gd.gece} Gece`;
  if (gd.tur === 'ucak' && UCAK_YON[gd.yon]) {
    return `${UCAK_YON[gd.yon].ad} ${[gd.gidis_tarihi, gd.donus_tarihi].filter(Boolean).map(tarih).join(' – ')}`;
  }
  return tarih(gd.tarih);
};

// Satırın başındaki tarih bloğu: üstte gün.ay (aralıksa →), altta gün adı ve ne tarihi olduğu
function GiderTarihi({ gd }) {
  let ust;
  let alt;
  if (gd.tur === 'konaklama') {
    ust = `${kisaGun(gd.giris)} → ${kisaGun(gd.cikis)}`;
    alt = `${gunAdiKisa(gd.giris)}–${gunAdiKisa(gd.cikis)} · ${gd.gece} Gece`;
  } else if (gd.tur === 'ucak' && UCAK_YON[gd.yon]) {
    const [a, b] = [gd.gidis_tarihi, gd.donus_tarihi];
    ust = a && b ? `${kisaGun(a)} → ${kisaGun(b)}` : kisaGun(a || b);
    alt = a && b ? `${gunAdiKisa(a)} – ${gunAdiKisa(b)}` : `${gunAdiKisa(a || b)} · Uçuş`;
  } else {
    ust = kisaGun(gd.tarih);
    alt = `${gunAdiKisa(gd.tarih)} · Belge`;
  }
  return (
    <div className="gd-tarih">
      <div className="gd-tarih-ust">{ust}</div>
      <div className="gd-tarih-alt">{alt}</div>
    </div>
  );
}

// Konaklama geceleri: hepsi aynı fiyatsa tek "Gecelik" tutar (meta satırında), farklıysa gece gece küçük kutular
const esitGecelik = (gd) => gd.tur === 'konaklama' && gd.geceler?.length > 1
  && gd.geceler.every((x) => x.tutar === gd.geceler[0].tutar);

function GeceKutulari({ gd }) {
  if (gd.tur !== 'konaklama' || !(gd.geceler?.length > 1) || esitGecelik(gd)) return null;
  return (
    <div className="gd-geceler">
      {gd.geceler.map((x) => (
        <span key={x.tarih} className="gd-gece-cip"><span>{gunAdiKisa(x.tarih)} {kisaGun(x.tarih)}</span><b>{para(x.tutar)}</b></span>
      ))}
    </div>
  );
}

const BELGE_IPUCU = { calisan: 'Rezervasyon Personeli Ekleyecek', rezervasyon: 'Ödeme Aşamasında Ekleyin' };

function GiderSatiri({ s, gd, onDuzenle, rol }) {
  const t = GIDER_TANIM[gd.tur] || { ad: gd.tur };
  const isaretler = s.gider_isaretleri?.[gd.id];
  const bekliyor = !gd.fis?.yol;
  const duzenle = onDuzenle ? () => onDuzenle(gd) : undefined;
  const dur = (e) => e.stopPropagation();
  const kimGirer = rol && GIDER_GIREN[gd.tur] !== rol
    ? (GIDER_GIREN[gd.tur] === 'rezervasyon' ? 'Rezervasyon Personeli Girer' : 'Çalışan Girer') : null;
  // Tarih satır başında; burada tekrar edilmez (uçakta yalnız biletin alındığı gün), açıklama ve kimin girdiği
  const meta = [gd.tur === 'ucak' && UCAK_YON[gd.yon] ? `Bilet Alımı ${tarih(gd.tarih)}` : null, gd.aciklama,
    esitGecelik(gd) ? `Gecelik ${para(gd.geceler[0].tutar)}` : null, kimGirer].filter(Boolean);
  return (
    <div className={`gd-satir${ciddiMi(isaretler) ? ' gd-isaretli' : ''}${bekliyor ? ' gd-bekliyor' : ''}${duzenle ? ' gd-tikla' : ''}`}
      role={duzenle ? 'button' : undefined} tabIndex={duzenle ? 0 : undefined} onClick={duzenle}
      onKeyDown={duzenle ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); duzenle(); } } : undefined}
      aria-label={duzenle ? `${t.ad} ${giderDonemi(gd)} ${para(gd.tutar)} — düzenle` : undefined}>
      <GiderTarihi gd={gd} />
      <div className="gd-resim" onClick={dur} onKeyDown={dur}>
        {bekliyor
          ? <div className="gd-belge-yok"><ClockCircleOutlined /></div>
          : <FisKucuk seyahatId={s.id} giderId={gd.id} fis={gd.fis} />}
      </div>
      <div className="gd-bilgi">
        <div className="gd-ad">
          <span className="gd-ikon">{t.ikon}</span>{t.ad}
          {gd.tur === 'ucak' && (UCAK_YON[gd.yon]
            ? <Tag color={UCAK_YON[gd.yon].renk} style={{ marginInlineEnd: 0, fontWeight: 600 }}>{UCAK_YON[gd.yon].ad}</Tag>
            : <Tag style={{ marginInlineEnd: 0 }}>Yön Girilmemiş</Tag>)}
        </div>
        {meta.length > 0 && <div className="gd-meta">{meta.join(' · ')}</div>}
        <GeceKutulari gd={gd} />
      </div>
      <div className="gd-tutar">{para(gd.tutar)}</div>
      <div className="gd-duzenle">{duzenle && <EditOutlined />}</div>
      {(bekliyor || isaretler?.length > 0) && (
        <div className="gd-isaret">
          {bekliyor && (
            <div className="gd-durum">
              <span className="gd-durum-rozet"><ClockCircleOutlined /> Belge Bekleniyor</span>
              {BELGE_IPUCU[rol] && <span className="gd-durum-ipucu">{BELGE_IPUCU[rol]}</span>}
            </div>
          )}
          <IsaretEtiketleri isaretler={isaretler}
            aciklamaAdi={GIDER_GIREN[gd.tur] === 'rezervasyon' ? 'Rezervasyon Personelinin Açıklaması' : 'Çalışanın Açıklaması'} />
        </div>
      )}
    </div>
  );
}

/**
 * Seyahatin ulaşım ve konaklama bölümü. rol: calisan | rezervasyon — o rolün girdiği türler eklenir / düzenlenir,
 * diğerleri salt okunur görünür. onEkle verilmezse tamamen salt okunur (yönetim, onaycı).
 */
export function GiderBolumu({ s, rol, onUlasim, onEkle, onDuzenle, baslik = true, not }) {
  const { token } = theme.useToken();
  const duzenlenebilir = !!onEkle;
  const ulasim = ULASIM_TANIM[s.ulasim];
  const eklenebilir = !duzenlenebilir ? [] : rol === 'calisan'
    ? (ulasim?.giderler || []).filter((t) => GIDER_GIREN[t] === 'calisan')
    : [...(ulasim?.giderler || []).filter((t) => GIDER_GIREN[t] === 'rezervasyon'), 'konaklama'];
  const satirDuzenle = (gd) => (onDuzenle && GIDER_GIREN[gd.tur] === rol ? onDuzenle : undefined);
  const giderler = [...(s.giderler || [])].sort((a, b) => (SIRA.indexOf(a.tur) - SIRA.indexOf(b.tur))
    || anaTarih(a).localeCompare(anaTarih(b)));
  const degiskenler = {
    '--gd-kenar': token.colorBorderSecondary, '--gd-kenar2': token.colorBorder, '--gd-hata': token.colorError,
    '--gd-hata-zemin': token.colorErrorBg, '--gd-ikincil': token.colorTextSecondary, '--gd-birincil': token.colorPrimary,
    '--gd-dolgu': token.colorFillQuaternary, '--gd-uyari': token.colorWarning,
    '--gd-uyari-zemin': token.colorWarningBg, '--gd-uyari-yazi': token.colorWarningText,
  };
  return (
    <div className="gd-bolum" style={degiskenler}>
      <style>{`
        .gd-bolum { margin: 4px 0 8px; }
        .gd-bas { display: flex; justify-content: space-between; align-items: baseline; gap: 4px 12px; flex-wrap: wrap; margin-bottom: 8px; }
        .gd-baslik { font-size: 12px; color: var(--gd-ikincil); text-transform: uppercase; letter-spacing: .04em; }
        .gd-ulasim { display: flex; align-items: center; gap: 8px 12px; flex-wrap: wrap; margin-bottom: 10px; }
        /* İşaret ayrı alanda: masaüstünde bilginin altında, telefonda tam genişlik alt satırda */
        .gd-satir { display: grid; grid-template-columns: 112px 44px minmax(0, 1fr) auto 14px; column-gap: 12px; align-items: center;
                    grid-template-areas: "tarih resim bilgi tutar duzen" "tarih resim isaret tutar duzen";
                    padding: 8px 10px; margin-bottom: 8px; border: 1px solid var(--gd-kenar); border-radius: 10px; }
        .gd-tarih { grid-area: tarih; align-self: stretch; display: flex; flex-direction: column; justify-content: center;
                    padding-right: 10px; border-right: 1px solid var(--gd-kenar); }
        .gd-tarih-ust { font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .gd-tarih-alt { font-size: 12px; color: var(--gd-ikincil); white-space: nowrap; }
        .gd-resim { grid-area: resim; }
        .gd-bilgi { grid-area: bilgi; min-width: 0; }
        .gd-tutar { grid-area: tutar; }
        .gd-duzenle { grid-area: duzen; }
        .gd-isaret { grid-area: isaret; min-width: 0; }
        .gd-satir.gd-tikla { cursor: pointer; }
        .gd-satir.gd-tikla:hover, .gd-satir.gd-tikla:focus-visible { border-color: var(--gd-birincil); outline: none; }
        .gd-isaretli { border: 2px solid var(--gd-hata); background: var(--gd-hata-zemin); }
        .gd-bekliyor { border-left: 3px solid var(--gd-uyari); padding-left: 8px; }
        .gd-durum { display: flex; align-items: center; gap: 4px 8px; flex-wrap: wrap; margin-top: 6px; font-size: 12px; }
        .gd-durum-rozet { display: inline-flex; align-items: center; gap: 5px; padding: 1px 9px; border-radius: 999px;
                          background: var(--gd-uyari-zemin); color: var(--gd-uyari-yazi); font-weight: 600; white-space: nowrap; }
        .gd-durum-ipucu { color: var(--gd-ikincil); }
        .gd-geceler { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
        .gd-gece-cip { display: inline-flex; align-items: baseline; gap: 6px; padding: 2px 8px; border-radius: 6px;
                       background: var(--gd-dolgu); border: 1px solid var(--gd-kenar); font-size: 12px; white-space: nowrap; }
        .gd-gece-cip > span { color: var(--gd-ikincil); }
        .gd-gece-cip > b { font-weight: 600; font-variant-numeric: tabular-nums; }
        .gd-belge-yok { width: 44px; height: 56px; border-radius: 4px; border: 1px dashed var(--gd-uyari); color: var(--gd-uyari);
                        display: flex; align-items: center; justify-content: center; font-size: 18px; }
        .gd-resim { min-height: 56px; display: flex; align-items: center; }
        .gd-ad { font-weight: 600; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .gd-ikon { display: inline-flex; font-size: 16px; color: var(--gd-ikincil); }
        .gd-meta { font-size: 12px; color: var(--gd-ikincil); margin-top: 2px; }
        .gd-tutar { font-size: 16px; font-weight: 700; font-variant-numeric: tabular-nums; text-align: right; }
        .gd-duzenle { font-size: 12px; color: var(--gd-ikincil); align-self: start; padding-top: 2px; }
        .gd-toplam { display: flex; justify-content: flex-end; gap: 4px 16px; flex-wrap: wrap; font-size: 13px;
                     color: var(--gd-ikincil); font-variant-numeric: tabular-nums; margin-top: 2px; }
        .gd-toplam b { color: ${token.colorText}; }
        .gd-ekle { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
        .gd-serit { display: flex; gap: 6px; overflow-x: auto; padding-bottom: 4px; margin-bottom: 10px; }
        .gd-gece { flex: 0 0 112px; border: 1px dashed var(--gd-kenar2); border-radius: 8px; padding: 6px 8px; font-size: 12px; }
        .gd-gece-dolu { border-style: solid; border-color: var(--gd-kenar); background: var(--gd-dolgu); }
        .gd-gece.gd-tikla { cursor: pointer; }
        .gd-gece.gd-tikla:hover, .gd-gece.gd-tikla:focus-visible { border-color: var(--gd-birincil); outline: none; }
        .gd-gece-gun { color: var(--gd-ikincil); }
        .gd-gece-otel { font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .gd-gece-tutar { font-variant-numeric: tabular-nums; }
        .gd-gece-bos { color: var(--gd-ikincil); padding-top: 9px; }
        @media (max-width: 640px) {
          .gd-satir { grid-template-columns: minmax(0, 1fr) auto 14px;
                      grid-template-areas: "tarih tutar duzen" "bilgi bilgi bilgi" "isaret isaret isaret"; }
          .gd-tarih { flex-direction: row; flex-wrap: wrap; align-items: baseline; justify-content: flex-start; gap: 0 8px;
                      min-width: 0; border-right: 0; padding: 0 0 4px; }
          .gd-tarih-alt { white-space: normal; }
          .gd-tutar { font-size: 15px; white-space: nowrap; }
          .gd-resim { display: none; }
        }
      `}</style>
      {baslik && (
        <div className="gd-bas">
          <span className="gd-baslik">Ulaşım Ve Konaklama</span>
          <Text type="secondary" style={{ fontSize: 12 }}>Şirket öder — çalışana geri ödenmez; maliyet takibi için belgesiyle girilir.</Text>
        </div>
      )}
      <div className="gd-ulasim">
        <Text>Ulaşım Şekli:</Text>
        {onUlasim ? (
          <Radio.Group optionType="button" buttonStyle="solid" value={s.ulasim || undefined} onChange={(e) => onUlasim(e.target.value)}
            options={Object.entries(ULASIM_TANIM).map(([k, u]) => ({ value: k, label: <span>{u.ikon} {u.ad}</span> }))} />
        ) : ulasim ? <Tag icon={ulasim.ikon} style={{ marginInlineEnd: 0 }}>{ulasim.ad}</Tag> : <Text type="secondary">Seçilmedi</Text>}
        {onUlasim && !s.ulasim && <Text type="danger" style={{ fontSize: 12 }}>Onaya göndermek için seçin</Text>}
        {s.konaklama_gerekli != null && (
          <Tag icon={<OtelIkon />} style={{ marginInlineEnd: 0 }}>{s.konaklama_gerekli ? 'Otel Gerekli' : 'Otel Gerekmiyor'}</Tag>
        )}
      </div>
      {(s.konaklama_gerekli || s.giderler?.some((gd) => gd.tur === 'konaklama')) && (
        <KonaklamaSeridi s={s}
          onGece={duzenlenebilir && rol === 'rezervasyon' ? (t) => onEkle('konaklama', { gece: t }) : undefined}
          onAc={duzenlenebilir && rol === 'rezervasyon' && onDuzenle ? onDuzenle : undefined} />
      )}
      {giderler.map((gd) => <GiderSatiri key={gd.id} s={s} gd={gd} rol={duzenlenebilir ? rol : undefined} onDuzenle={satirDuzenle(gd)} />)}
      {!giderler.length && !duzenlenebilir && <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Ulaşım / konaklama gideri girilmedi.</Text>}
      {duzenlenebilir && (
        <div className="gd-ekle">
          {eklenebilir.map((tur) => (
            <Button key={tur} icon={<PlusOutlined />} onClick={() => onEkle(tur)}>
              {GIDER_TANIM[tur].ekle}{tur === 'konaklama' && !s.konaklama_gerekli && <Text type="secondary" style={{ fontSize: 12 }}>&nbsp;(İsteğe Bağlı)</Text>}
            </Button>
          ))}
          <Text type="secondary" style={{ fontSize: 12, alignSelf: 'center' }}>
            {not || (rol === 'calisan'
              ? 'Uçak bileti, araç kiralama ve oteli rezervasyon personeli girer.'
              : 'Yakıt fişlerini çalışan girer. Belge hazır değilse tutarla kaydedin, gelince ekleyin.')}
          </Text>
        </div>
      )}
      {giderler.length > 0 && (
        <div className="gd-toplam">
          <span>Ulaşım {para(s.ulasim_toplam)}</span>
          <span>Konaklama {para(s.konaklama_toplam)}</span>
          <span><b>Şirket Giderleri {para(s.gider_toplam)}</b></span>
        </div>
      )}
    </div>
  );
}

/** Gider ekle / düzenle: belge seçilince (rıza varsa) Claude ile okunur, tutar ve tarih öneri olarak gelir. */
export function GiderPenceresi({ istek, okumaAcik, onKapat, onKaydedildi, kok = '/harcirah-benim', belgeZorunlu = true }) {
  const api = useHarcirahApi(kok);
  const { message, modal } = App.useApp();
  const [form] = Form.useForm();
  const [dosya, setDosya] = useState(null);
  const [okuma, setOkuma] = useState(null); // {okuma, uyarilar, jeton} | {engel}
  const [okunuyor, setOkunuyor] = useState(false);
  const [bekliyor, setBekliyor] = useState(false);
  const [geceler, setGeceler] = useState([]);  // konaklama: [{tarih, tutar}]
  const tutarAlani = Form.useWatch('tutar', form);
  const donemDegeri = Form.useWatch('donem', form);
  const yonDegeri = Form.useWatch('yon', form);
  const farkDegeri = Form.useWatch('fark_aciklama', form) || '';
  const s = istek?.seyahat;
  const gd = istek?.gider;
  const tur = istek?.tur;
  const t = GIDER_TANIM[tur] || {};
  const konaklama = tur === 'konaklama';
  const ucak = tur === 'ucak';
  const bas = s && dayjs(s.baslangic);
  const bit = s && dayjs(s.bitis);

  useEffect(() => {
    if (!istek) return;
    setDosya(null);
    setOkuma(null);
    // Konaklama: kayıtlı gecelerden; boş geceden açıldıysa o gece; değilse seyahat boyunca
    const donem = !konaklama ? null : gd?.giris ? [dayjs(gd.giris), dayjs(gd.cikis)]
      : istek.gece ? [dayjs(istek.gece), dayjs(istek.gece).add(1, 'day')]
        : [bas, bit.isAfter(bas) ? bit : bas.add(1, 'day')];
    setGeceler(konaklama ? (gd?.geceler || geceKur(donem, [], null)) : []);
    form.setFieldsValue({
      tutar: gd?.tutar ?? null,
      tarih: gd?.tarih ? dayjs(gd.tarih) : null,
      donem,
      gecelik: null,
      aciklama: gd?.aciklama || '',
      fark_aciklama: gd?.fis?.aciklama || '',
      // Uçak: yön ve uçuş tarihleri (yeni bilette seyahatin ilk / son günü önerilir)
      yon: gd?.yon,
      gidis_tarihi: gd?.gidis_tarihi ? dayjs(gd.gidis_tarihi) : bas,
      donus_tarihi: gd?.donus_tarihi ? dayjs(gd.donus_tarihi) : bit,
    });
  }, [istek]); // eslint-disable-line react-hooks/exhaustive-deps

  const geceToplam = Math.round(geceler.reduce((a, x) => a + (x.tutar || 0), 0) * 100) / 100;
  const girilen = konaklama ? (geceler.length ? geceToplam : null) : tutarAlani;
  const donemDegisti = (v) => setGeceler((l) => geceKur(v, l, form.getFieldValue('gecelik')));
  const geceFiyati = (t, v) => setGeceler((l) => l.map((x) => (x.tarih === t ? { ...x, tutar: v } : x)));

  // Belge tarihi: seyahatten 60 gün önce (önceden alınan bilet) – 7 gün sonra; konaklama: seyahat ±1 gün
  const tarihDisi = (d) => d.isBefore(bas.subtract(60, 'day'), 'day') || d.isAfter(bit.add(7, 'day'), 'day');
  const donemDisi = (d) => d.isBefore(bas.subtract(1, 'day'), 'day') || d.isAfter(bit.add(1, 'day'), 'day');

  const okut = (k, itiraz = false) => {
    const onceki = okuma?.okuma?.toplam;
    setOkunuyor(true);
    const fd = new FormData();
    fd.append('tur', tur);
    fd.append('fis', k);
    if (gd) fd.append('gider_id', gd.id);
    if (itiraz) fd.append('itiraz', 'true');
    api.form(`/${s.id}/gider-oku`, fd).then((d) => {
      setOkuma(d);
      if (!d.okuma?.okunabildi) return;
      const v = form.getFieldsValue();
      if (v.tutar == null || v.tutar === gd?.tutar || (onceki != null && v.tutar === onceki)) form.setFieldsValue({ tutar: d.okuma.toplam });
      const okunanTarih = d.okuma.tarih && dayjs(d.okuma.tarih);
      if (!konaklama && okunanTarih?.isValid() && !tarihDisi(okunanTarih) && (!v.tarih || itiraz)) form.setFieldsValue({ tarih: okunanTarih });
      // Konaklama: belgede dönem varsa giriş–çıkış odur (seyahat penceresi içindeyse; değilse fark aşağıda gösterilir).
      // Gecelerin fiyatı boşsa okunan toplam gecelere eşit dağıtılır (son gece kuruş farkını alır).
      if (konaklama) {
        let donem = form.getFieldValue('donem');
        if (d.okuma.giris && d.okuma.cikis) {
          const okunanDonem = [dayjs(d.okuma.giris), dayjs(d.okuma.cikis)];
          if (!okunanDonem.some(donemDisi)) {
            donem = okunanDonem;
            form.setFieldsValue({ donem });
          }
        }
        setGeceler((l) => {
          const yeni = geceKur(donem, l, null);
          if (!yeni.length || yeni.some((x) => x.tutar != null)) return yeni;
          const pay = Math.floor((d.okuma.toplam / yeni.length) * 100) / 100;
          return yeni.map((x, i) => ({ ...x, tutar: i === yeni.length - 1 ? Math.round((d.okuma.toplam - pay * (yeni.length - 1)) * 100) / 100 : pay }));
        });
      }
    }).catch((e) => {
      setOkuma({ engel: hata(e) });
      if (e?.response?.status === 409) setDosya(null);
    }).finally(() => setOkunuyor(false));
  };

  const dosyaSecildi = async (f) => {
    const k = await fisiKucult(f);
    setDosya(k);
    setOkuma(null);
    if (okumaAcik) okut(k);
  };

  const o = okuma?.okuma;
  // Karşılaştırılan okuma: yeni belge seçildiyse onunki, değilse kayıtlı belgeninki
  const okunan = dosya ? (o?.okunabildi ? o : null) : (gd?.fis?.okuma?.okunabildi ? gd.fis.okuma : null);
  const okunanToplam = okunan ? okunan.toplam : null;
  const farkli = okunanToplam != null && girilen != null && Math.abs(girilen - okunanToplam) >= 0.01;
  // Konaklama tarihi (backend konaklama_tarih_isareti ile aynı kural): belgedeki dönem = girilen dönem; tek tarihli
  // belge seyahat tarihleri içinde (±1 gün)
  let tarihFarki = null;
  if (konaklama && okunan && donemDegeri?.[0] && donemDegeri?.[1]) {
    if (okunan.giris && okunan.cikis) {
      if (okunan.giris !== gun(donemDegeri[0]) || okunan.cikis !== gun(donemDegeri[1])) {
        tarihFarki = { tur: 'tarih', okunan_giris: okunan.giris, okunan_cikis: okunan.cikis,
          girilen_giris: gun(donemDegeri[0]), girilen_cikis: gun(donemDegeri[1]),
          disinda: [okunan.giris, okunan.cikis].some((x) => donemDisi(dayjs(x))) };
      }
    } else if (okunan.tarih && donemDisi(dayjs(okunan.tarih))) {
      tarihFarki = { tur: 'tarih', okunan: okunan.tarih, seyahat_bas: s.baslangic, seyahat_bit: s.bitis };
    }
  }
  const nedenTurleri = [...(farkli ? ['tutar'] : []), ...(tarihFarki ? ['tarih'] : [])];
  const geceSayisi = donemDegeri?.[0] && donemDegeri?.[1] ? donemDegeri[1].diff(donemDegeri[0], 'day') : 0;
  const ayni = (okuma?.uyarilar || []).find((u) => u.tur === 'ayni');
  const nedenSec = (metin) => {
    const parcalar = farkDegeri.split(';').map((x) => x.trim()).filter(Boolean);
    const yeni = parcalar.includes(metin) ? parcalar.filter((x) => x !== metin) : [...parcalar, metin];
    form.setFieldsValue({ fark_aciklama: yeni.join('; ') });
  };

  const kaydet = () => form.validateFields().then((v) => {
    if (!dosya && !gd && belgeZorunlu) {
      message.warning('Belgeyi (fiş / fatura / e-bilet) ekleyin.');
      return null;
    }
    if (konaklama && (!geceler.length || geceler.some((x) => !(x.tutar > 0)))) {
      message.warning('Her gecenin fiyatını girin.');
      return null;
    }
    const fd = new FormData();
    fd.append('tur', tur);
    fd.append('tutar', String(konaklama ? geceToplam : v.tutar));
    if (gd) fd.append('gider_id', gd.id);
    if (konaklama) {
      fd.append('giris', v.donem[0].format('YYYY-MM-DD'));
      fd.append('cikis', v.donem[1].format('YYYY-MM-DD'));
      fd.append('geceler', JSON.stringify(geceler));
    } else {
      fd.append('tarih', v.tarih.format('YYYY-MM-DD'));
    }
    if (ucak) {
      fd.append('yon', v.yon);
      UCAK_YON[v.yon].tarihler.forEach((k) => fd.append(`${k}_tarihi`, v[`${k}_tarihi`].format('YYYY-MM-DD')));
    }
    if ((v.aciklama || '').trim()) fd.append('aciklama', v.aciklama.trim());
    if (nedenTurleri.length && (v.fark_aciklama || '').trim()) fd.append('fark_aciklama', v.fark_aciklama.trim());
    if (dosya) fd.append('fis', dosya);
    if (dosya && okuma?.jeton) fd.append('okuma', okuma.jeton);
    setBekliyor(true);
    return api.form(`/${s.id}/gider`, fd)
      .then((y) => { message.success('Kaydedildi.'); onKaydedildi(y); })
      .catch((e) => message.error(hata(e)))
      .finally(() => setBekliyor(false));
  }).catch(() => {});

  const sil = () => modal.confirm({
    title: `${t.ad} gideri silinsin mi?`, content: `${para(gd.tutar)} · ${giderDonemi(gd)}`, okText: 'Sil', okButtonProps: { danger: true },
    cancelText: 'Vazgeç', className: 'hr-modal',
    onOk: () => api.del(`/${s.id}/gider/${gd.id}`).then((y) => { message.success('Silindi.'); onKaydedildi(y); })
      .catch((e) => message.error(hata(e))),
  });


  return (
    <Modal rootClassName="hr-modal" open={!!istek} onCancel={onKapat} destroyOnHidden
      title={istek ? <Space>{t.ikon}{gd ? `${t.ad} — Düzenle` : `${t.ad} Ekle`}</Space> : ''}
      footer={[
        gd && <Button key="sil" danger icon={<DeleteOutlined />} onClick={sil} style={{ float: 'left' }}>Gideri Sil</Button>,
        <Button key="vazgec" onClick={onKapat}>Vazgeç</Button>,
        <Button key="kaydet" type="primary" loading={bekliyor} disabled={okunuyor} onClick={kaydet}>Kaydet</Button>,
      ].filter(Boolean)}>
      {istek && (
        <Form form={form} layout="vertical">
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={belgeZorunlu ? 'Bu gideri şirket öder; size geri ödenmez. Maliyet takibi için belgesiyle girin.'
              : 'Bu gideri şirket öder. Fiyat aşamasında belge şart değil; ödemeyi yapınca bilet / faturayı ekleyin (finans kontrolüne göndermek için gerekli).'} />
          <Form.Item label={`${konaklama ? 'Otel Faturası' : 'Belge (Fiş / Fatura / E-Bilet)'}${belgeZorunlu ? '' : ' — Sonra Da Eklenebilir'}`}
            required={belgeZorunlu}
            extra={gd?.fis?.yol ? 'Yeni belge seçmezseniz kayıtlı belge kalır.' : 'JPG, PNG, HEIC ya da PDF; en fazla 10 MB.'}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {gd?.fis?.yol && (
                <Space>
                  <Text type="secondary">Kayıtlı Belge:</Text>
                  <FisDugmesi seyahatId={s.id} giderId={gd.id} fis={gd.fis} etiket="Göster" />
                </Space>
              )}
              {gd && !gd.fis?.yol && !dosya && <Tag color="warning" icon={<ClockCircleOutlined />}>BELGE BEKLENİYOR</Tag>}
              <Upload accept="image/*,application/pdf" maxCount={1}
                fileList={dosya ? [{ uid: '1', name: dosya.name, status: 'done' }] : []}
                beforeUpload={(f) => {
                  if (f.size > MAKS_FIS) {
                    message.error('Dosya 10 MB’tan büyük.');
                    return Upload.LIST_IGNORE;
                  }
                  dosyaSecildi(f);
                  return false;
                }}
                onRemove={() => { setDosya(null); setOkuma(null); }}>
                <Button icon={<CameraOutlined />} size="large" block style={{ whiteSpace: 'normal', height: 'auto', minHeight: 40, textAlign: 'left' }}>
                  {gd?.fis?.yol ? 'Belgeyi Değiştir' : `${konaklama ? 'Fatura' : 'Belge'} Fotoğrafı Çek / Seç${okumaAcik ? (konaklama ? ' (Tutar Ve Tarihler Okunur)' : ' (Tutar Okunur)') : ''}`}
                </Button>
              </Upload>
            </Space>
          </Form.Item>
          {okunuyor && <Alert type="info" showIcon icon={<LoadingOutlined />} style={{ marginBottom: 12 }} message="Belge okunuyor…" />}
          {!okumaAcik && dosya && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Otomatik okuma kapalı (açık rıza verilmedi); tutarı belgeye bakarak girin." />}
          {okuma?.engel && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={okuma.engel} />}
          {o && !o.okunabildi && (
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message={o.hata || `Belge okunamadı${o.not ? `: ${o.not}` : ''}; tutarı elle girin.`}
              action={o.kademe !== 'itiraz' && !okunuyor && !o.riza_gerekli && !o.hata?.includes('yapılandırılmamış')
                && !o.hata?.includes('HEIC')
                ? <Button size="small" onClick={() => okut(dosya, true)}>Gelişmiş Modelle Oku</Button> : null} />
          )}
          {o?.okunabildi && (
            <Alert type="success" showIcon icon={<ScanOutlined />} style={{ marginBottom: 12 }}
              message={(
                <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
                  <span>Belgeden Okundu: {para(o.toplam)}</span>
                  {o.kademe === 'ilk' && !okunuyor && (
                    <Button size="small" type="link" style={{ padding: 0 }} onClick={() => okut(dosya, true)}>Yanlış Mı Okundu? Tekrar Oku</Button>
                  )}
                  {o.kademe !== 'ilk' && <Tag color="blue">Gelişmiş Modelle Okundu</Tag>}
                </Space>
              )}
              description={(
                <div style={{ fontSize: 12 }}>
                  {[o.tarih && tarih(o.tarih), o.isletme, [o.ilce, o.il].filter(Boolean).join(' / ')].filter(Boolean).join(' · ')}
                  {konaklama && (
                    <div>{o.giris
                      ? <>Konaklama Dönemi: <b>{tarih(o.giris)} – {tarih(o.cikis)}</b> ({dayjs(o.cikis).diff(dayjs(o.giris), 'day')} Gece)</>
                      : 'Belgede konaklama dönemi yok; giriş–çıkışı siz seçin (belge tarihi seyahat içinde olmalı).'}
                    </div>
                  )}
                </div>
              )} />
          )}
          {ayni && (
            <Alert type="error" showIcon style={{ marginBottom: 12, borderWidth: 2 }}
              message={<b>{ISARET_TANIM.ayni.ad} — bu belge başka yerde de kullanılmış</b>}
              description={<span>{ISARET_TANIM.ayni.ayrinti(ayni)}. Yanlış belgeyse değiştirin; kaydederseniz onaycılar görecek.</span>} />
          )}
          {konaklama ? (
            <>
              <Form.Item name="aciklama" label="Otel" rules={[{ required: true, whitespace: true, message: 'Otel adını yazın' }]}>
                <Input maxLength={150} placeholder="Otel Adı" />
              </Form.Item>
              <Form.Item name="donem" label={`Giriş – Çıkış${geceSayisi > 0 ? ` · ${geceSayisi} Gece` : ''}`}
                extra="Tek otelde kalınacak aralığı seçin; geceler aşağıda dolar. Farklı otel için ayrı konaklama ekleyin."
                rules={[{ required: true, message: 'Giriş ve çıkış tarihini seçin' },
                  { validator: (_, v) => (!v || v[1].isAfter(v[0], 'day') ? Promise.resolve() : Promise.reject(new Error('Çıkış girişten sonra olmalı'))) }]}>
                <RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} disabledDate={donemDisi} placeholder={['Giriş', 'Çıkış']}
                  onChange={donemDegisti} />
              </Form.Item>
              <Form.Item label="Gecelik Fiyat" style={{ marginBottom: 8 }}>
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="gecelik" noStyle>
                    <InputNumber {...inputNumberMoneyProps} min={0} precision={2} suffix="₺" style={{ width: '100%' }} inputMode="decimal"
                      placeholder="Aynı fiyatsa bir kez yazın" />
                  </Form.Item>
                  <Button onClick={() => { const f = form.getFieldValue('gecelik'); if (f > 0) setGeceler((l) => l.map((x) => ({ ...x, tutar: f }))); }}>
                    Tüm Gecelere Uygula
                  </Button>
                </Space.Compact>
              </Form.Item>
              <div className="gp-geceler" style={{ marginBottom: 12 }}>
                {geceler.map((x) => (
                  <div key={x.tarih} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 160px', gap: 8, alignItems: 'center', padding: '3px 0' }}>
                    <Text>{gunAdiKisa(x.tarih)} {tarih(x.tarih)} <Text type="secondary" style={{ fontSize: 12 }}>Gecesi</Text></Text>
                    <InputNumber {...inputNumberMoneyProps} min={0} precision={2} suffix="₺" size="small" value={x.tutar}
                      onChange={(v) => geceFiyati(x.tarih, v)} status={x.tutar > 0 ? undefined : 'warning'} inputMode="decimal"
                      aria-label={`${tarih(x.tarih)} gecesinin fiyatı`} />
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(128,128,128,0.25)', marginTop: 4, paddingTop: 6 }}>
                  <Text strong>Toplam ({geceler.length} Gece)</Text>
                  <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>{para(geceToplam)}</Text>
                </div>
              </div>
            </>
          ) : (
            <>
              {ucak && (
                <>
                  <Form.Item name="yon" label="Bilet" rules={[{ required: true, message: 'Gidiş, dönüş ya da gidiş-dönüş seçin' }]}>
                    <Radio.Group optionType="button" buttonStyle="solid"
                      options={Object.entries(UCAK_YON).map(([k, y]) => ({ value: k, label: y.ad }))} />
                  </Form.Item>
                  {UCAK_YON[yonDegeri] && (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {UCAK_YON[yonDegeri].tarihler.map((k) => (
                        <Form.Item key={k} name={`${k}_tarihi`} label={`${UCAK_YON[k].ad} Uçuşu`} style={{ flex: '1 1 200px' }}
                          dependencies={k === 'donus' ? ['gidis_tarihi'] : []}
                          rules={[{ required: true, message: 'Uçuş tarihini seçin' },
                            ({ getFieldValue }) => ({
                              validator: (_, v) => (k !== 'donus' || yonDegeri !== 'gidis_donus' || !v || !getFieldValue('gidis_tarihi')
                                || !v.isBefore(getFieldValue('gidis_tarihi'), 'day')
                                ? Promise.resolve() : Promise.reject(new Error('Dönüş gidişten önce olamaz'))),
                            })]}>
                          <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} disabledDate={donemDisi} />
                        </Form.Item>
                      ))}
                    </div>
                  )}
                </>
              )}
              <Form.Item name="tutar" label="Tutar"
                rules={[{ required: true, message: 'Tutar girin' },
                  { validator: (_, v) => (v == null || v > 0 ? Promise.resolve() : Promise.reject(new Error('Tutar 0’dan büyük olmalı'))) }]}>
                <InputNumber {...inputNumberMoneyProps} min={0} precision={2} suffix="₺" style={{ width: '100%' }} size="large" inputMode="decimal" />
              </Form.Item>
              <Form.Item name="tarih" label={ucak ? 'Bilet (Belge) Tarihi' : 'Belge Tarihi'} rules={[{ required: true, message: 'Belge tarihini seçin' }]}
                extra={ucak ? 'Biletin alındığı gün (uçuş tarihi değil).' : null}>
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} disabledDate={tarihDisi} />
              </Form.Item>
            </>
          )}
          {tarihFarki && (
            <Alert type="error" showIcon style={{ marginTop: -12, marginBottom: 12, borderWidth: 2 }}
              message={<b style={{ fontSize: 15 }}>{ISARET_TANIM.tarih.ad} — belge bu konaklamayla uyuşmuyor</b>}
              description={(
                <div>
                  <b>{ISARET_TANIM.tarih.ayrinti(tarihFarki)}</b>
                  {tarihFarki.disinda && <div>Belgedeki dönem seyahat tarihlerinin dışında; giriş–çıkışı seyahate göre seçtik.</div>}
                  <div style={{ marginTop: 4 }}>Açıklama gerekli; onaycılar bu farkı görecek.</div>
                </div>
              )} />
          )}
          {farkli && (
            <Alert type="warning" showIcon style={{ marginTop: tarihFarki ? 0 : -12, marginBottom: 12 }}
              message={<span><b>TUTAR FARKLI</b> — belgeden {para(okunanToplam)} okundu, siz {para(girilen)} girdiniz. Açıklama gerekli; onaycılar bu farkı görecek.</span>} />
          )}
          {nedenTurleri.length > 0 && (
            <Form.Item label="Fark Açıklaması (Zorunlu)" required style={{ marginBottom: 28 }}>
              {nedenTurleri.map((tr) => (
                <div key={tr} style={{ marginBottom: 6 }}>
                  {nedenTurleri.length > 1 && <Text type="secondary" style={{ fontSize: 12, marginRight: 6 }}>{ISARET_TANIM[tr].ad}:</Text>}
                  <Space size={[4, 4]} wrap style={{ display: 'inline-flex' }}>
                    {GIDER_NEDENLERI[tr].map((n) => (
                      <Tag.CheckableTag key={n} checked={farkDegeri.split(';').map((x) => x.trim()).includes(n)}
                        onChange={() => nedenSec(n)} style={{ border: '1px solid rgba(128,128,128,0.35)', whiteSpace: 'normal' }}>
                        {n}
                      </Tag.CheckableTag>
                    ))}
                  </Space>
                </div>
              ))}
              <Form.Item name="fark_aciklama" noStyle rules={[{ required: true, min: 3, message: 'Bir neden seçin ya da açıklama yazın' }]}>
                <Input.TextArea rows={2} maxLength={300} showCount placeholder="Neden seçin ya da kendiniz yazın" />
              </Form.Item>
            </Form.Item>
          )}
          {!konaklama && (
            <Form.Item name="aciklama" label="Açıklama (İsteğe Bağlı)" style={{ marginBottom: 0 }}>
              <Input maxLength={150} placeholder={t.ipucu} />
            </Form.Item>
          )}
        </Form>
      )}
    </Modal>
  );
}
